<template>
    <Modal ref="Modal" :title="$t('labels.assign_values_to_attributes')" size="lg" @on-hide="hide">
        <div class="modal-body">
            <template v-if="task.state.isRunning">
                <TaskProgressBar :done="task.state.done" :total="task.state.total"/>
            </template>
            <template v-else>
                <FormGroup :label="$t('labels.occupancy_filter')">
                    <AppSelect
                        :model-value="selectedOccupancyFilter.key"
                        :selected-value="selectedOccupancyFilter.label"
                        :options="occupancyFilters"
                        @update:model-value="selectedOccupancyFilter =  $event"
                        option-key="key"
                        option-value="label"
                    />
                </FormGroup>
                <div class="table-responsive">
                    <table class="table table-bordered table-fixed">
                        <thead>
                            <tr>
                                <th width="250px">{{ $t('labels.filter') }}</th>
                                <th width="250px">{{ $t('labels.filter_item') }}</th>
                                <th width="250px">{{ $t('labels.action') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(filter, index) in filteredFilters">
                                <tr>
                                    <td>{{ filter.name }}</td>
                                    <td>
                                        <u v-if="filterItemsByFilterUuid[filter.uuid] === undefined" class="text-muted">{{ $t('labels.empty') }}</u>
                                        <span v-else>{{ filterItemsByFilterUuid[filter.uuid].map(item => item.name).join(', ') }}</span>
                                    </td>
                                    <td>
                                        <AppSelect
                                            :model-value="selectedAttributesForUpdate[filter.uuid]?.action?.key"
                                            :selected-value="selectedAttributesForUpdate[filter.uuid]?.action?.label"
                                            :options="getFilterItemsActionByIsMultiple(filter.is_multiple)"
                                            option-key="key"
                                            option-value="label"
                                            @update:model-value="(action) => selectAction(filter, action)"
                                            can-be-empty
                                        />
                                    </td>
                                </tr>
                                <tr v-if="selectedAttributesForUpdate[filter.uuid] && selectedAttributesForUpdate[filter.uuid].action.key !== 'clear'">
                                    <td colspan="3">
                                        <CatalogAttributeSelect
                                            :attribute="filter"
                                            :integration="integration"
                                            :category="category"
                                            :model-value="selectedAttributesForUpdate[filter.uuid].filterItems"
                                            @update:model-value="selectedAttributesForUpdate[filter.uuid].filterItems = $event"
                                        />
                                    </td>
                                </tr>
                            </template>
                        </tbody>    
                    </table>
                </div>
            </template>
        </div>
        <div class="modal-footer d-flex justify-content-end" v-if="!task.state.isRunning">
            <Button color="indigo" @click="saveProductsFilterItems" :disabled="Object.keys(selectedAttributesForUpdate).length === 0">
                {{ $t("buttons.save") }}
            </Button>
        </div>
    </Modal>
</template>
<script>
import Modal from "../../../../components/new/modal/AppModal.vue";
import TaskProgressBar from "../../../../components/new/progress/TaskProgressBar.vue";
import CatalogAttributeSelect from "../sidebarCards/products/edit/CatalogAttributeSelect.vue";
import Button from "../../../../components/new/button/AppButton.vue";
import AppCheckbox from "../../../../components/new/form/checkbox/AppCheckbox.vue";
import AppSelect from "../../../../components/new/form/select/AppSelect.vue";

import { httpClient } from "../../../../services/HttpClient";
import HttpRequest from "../../../../services/Http/HttpRequest";
import HttpSender from "../../../../services/Http/HttpSender";
import FormGroup from "../../../../components/new/form/group/FormGroup.vue";

import { createProgressTask } from "../../../../services/Progress/ProgressTask";

export default {
    name: "AssignValuesToAttributesModal",
    components: {
        Modal, TaskProgressBar, CatalogAttributeSelect, Button, AppCheckbox,
        AppSelect, FormGroup
    },
    props: {
        uuids: {
            type: Array,
            required: true
        },
        totalProductsCount: {
            type: Number,
            default: 0
        },
        tableSearch: {
            type: String,
            default: ''
        },
        tableOrders: {
            type: Array,
            default: () => ([])
        },
        integration: {
            type: Object,
            default: () => ({})
        },
        category: {
            type: Object,
            required: true
        },
        productFilters: {
            type: Object,
            default: () => ({})
        },
        getFilterName: {
            type: Function,
            required: true
        },
        productsListUrl: {
            type: String,
            default: null
        }
    },
    emits: [
        "close",
        "updated:filter-items"
    ],
    data() {
        return {
            task: createProgressTask(),
            products: [],
            filters: [],
            totalFiltersCount: 0,
            filterItemsByFilterUuid: null,
            selectedAttributesForUpdate: {},
            selectedForUpdateFilters: [],
            occupancyFilters: [
                { key: 'all', label: this.$t('labels.all') },
                { key: 'only_filled', label: this.$t('labels.only_filled') },
                { key: 'only_empty', label: this.$t('labels.only_empty') }
            ],
            selectedOccupancyFilter: {
                key: 'all',
                label: this.$t('labels.all')
            }
        }
    },
    methods: {
        getFilterItemsActionByIsMultiple(isMultiple) {
            let defaultActions = [
                { key: 'change', label: this.$t('assignValueToAttributesModal.actions.change') },
                { key: 'clear', label: this.$t('assignValueToAttributesModal.actions.clear') }
            ];

            if (isMultiple) {
                defaultActions.push({ key: 'add', label: this.$t('assignValueToAttributesModal.actions.add') });
                defaultActions.push({ key: 'remove', label: this.$t('assignValueToAttributesModal.actions.remove') });
            }

            return defaultActions;
        },
        loadProducts() {
            return this.task.run(async (progress) => {
                const selectedProductUuids = this.uuids.length > 0
                    ? this.uuids
                    : await this.fetchProductUuidsByTableFilters(progress);

                await this.fetchTotalFiltersCount(progress.signal);
                progress.tick();

                await this.fetchFilters(progress.signal);
                progress.tick();

                await this.fetchProducts(selectedProductUuids, progress);
            });
        },
        hide() {
            this.$emit("close");
        },
        buildTableListParams() {
            let params = {...this.productFilters};

            if (this.tableSearch) {
                params.search = this.tableSearch;
            }

            if (this.tableOrders.length > 0) {
                params.orders = this.tableOrders;
            }

            return params;
        },
        async fetchProductUuidsByTableFilters(progress) {
            if (!this.productsListUrl) {
                return [];
            }

            const listParams = this.buildTableListParams();
            const pageSize = 50;

            let totalProducts = this.totalProductsCount;

            if (!totalProducts) {
                const response = await httpClient.get(this.productsListUrl, {...listParams, limit: 1});
                totalProducts = response.data.meta.total;
            }

            if (!totalProducts) {
                return [];
            }

            let paramsByPage = [];

            for (let page = 1; page <= Math.ceil(totalProducts / pageSize); page++) {
                paramsByPage.push({...listParams, limit: pageSize, page: page});
            }

            const responses = await this.fetchPages(this.productsListUrl, paramsByPage, progress);
            const uniqueProductUuids = new Set();

            responses.forEach(response => {
                response.data.data.forEach(product => uniqueProductUuids.add(product.uuid));
            });

            const productUuids = Array.from(uniqueProductUuids);

            return productUuids;
        },
        async fetchPages(url, paramsByPage, {signal, tick}) {
            const concurrency = 10;
            const responses = [];

            for (let i = 0; i < paramsByPage.length; i += concurrency) {
                if (signal.aborted) {
                    break;
                }

                const chunkedParams = paramsByPage.slice(i, i + concurrency);
                const pendingResponses = chunkedParams.map(params => httpClient.get(url, params));
                const chunkedResponses = await Promise.all(pendingResponses);

                chunkedResponses.forEach(response => {
                    tick();
                    responses.push(response);
                });
            }

            return responses;
        },
        async fetchProducts(uuids = [], progress) {
            let paramsByPage = [];

            for (let i = 0; i < uuids.length; i += 10) {
                const chunkedUuids = uuids.slice(i, i + 10);

                paramsByPage.push({
                    uuids: chunkedUuids,
                    category_uuids: [this.$route.params.categoryUuid],
                    extra_fields: ['integrationFilterItems'],
                    limit: chunkedUuids.length
                });
            }

            let products = [];
            let filterItemsUsageStats = {};
            let filterItemsByUuid = {};
            let filterItemsByFilterUuid = {};

            const responses = await this.fetchPages(
                `/integrations/${this.$route.params.integrationUuid}/products`,
                paramsByPage,
                progress,
            );

            responses.forEach(response => {
                for (let product of response.data.data) {
                    for (const filterItem of product.integration_filter_items) {
                        const filterUuid = filterItem.integration_filter_uuid;
                        const filterItemUuid = filterItem.uuid;
                        
                        filterItemsByUuid[filterItemUuid] = filterItem;

                        if (!filterItemsUsageStats[filterUuid]) {
                            filterItemsUsageStats[filterUuid] = {};
                        }
                        if (!filterItemsUsageStats[filterUuid][filterItemUuid]) {
                            filterItemsUsageStats[filterUuid][filterItemUuid] = 0;
                        }
                        filterItemsUsageStats[filterUuid][filterItemUuid]++;
                    }
                    products.push(product);
                }
            });
            
            const totalProducts = products.length;

            for (const [filterUuid, itemsMap] of Object.entries(filterItemsUsageStats)) {
                const itemsUsedInAllProducts = Object.entries(itemsMap)
                    .filter(([, count]) => count === totalProducts)
                    .map(([filterItemUuid]) => filterItemsByUuid[filterItemUuid]);

                if (itemsUsedInAllProducts.length !== Object.entries(itemsMap).length) {
                    continue;
                }
                
                if (itemsUsedInAllProducts.length > 0) {
                    filterItemsByFilterUuid[filterUuid] = itemsUsedInAllProducts;
                }
            }
            
            if (progress.signal.aborted) {
                return;
            }

            this.filterItemsByFilterUuid = filterItemsByFilterUuid;
            this.products = products;
        },
        async fetchFilters(signal) {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, {
                integration_category_uuids: [this.$route.params.categoryUuid],
                limit: 0,
                has_default_items: 0,
                orders: [{
                    column: "name",
                    dir: "asc"
                }]
            }).then((response) => {
                if (signal.aborted) {
                    return;
                }

                this.filters.push(...response.data.data.map(filter => ({
                    ...filter,
                    sourceId: filter.source_id,
                    name: this.getFilterName(filter)
                })));
            });
        },
        async fetchTotalFiltersCount(signal) {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, {
                integration_category_uuids: [this.$route.params.categoryUuid],
                limit: 1
            }).then(response => {
                if (signal.aborted) {
                    return;
                }

                this.totalFiltersCount = response.data.meta.total;
            });
        },
        selectAction(filter, action) {
            if (!action) {
                delete this.selectedAttributesForUpdate[filter.uuid];
                return;
            }

            let forUpdateData = {
                action: action,
                filterIsMultiple: filter.is_multiple,
                filterItems: []
            };

            if (action.key === 'change' && this.filterItemsByFilterUuid[filter.uuid]) {
                forUpdateData.filterItems = [...this.filterItemsByFilterUuid[filter.uuid]];
            }

            this.selectedAttributesForUpdate[filter.uuid] = forUpdateData;
        },
        buildSaveRequests() {
            return this.products.map((product) => {
                let productFilterItemUuidsByFilterUuid = product.integration_filter_items
                    .reduce((acc, filterItem) => {
                        const filterUuid = filterItem.integration_filter_uuid;
                        const filterItemUuid = filterItem.uuid;

                        if (!acc[filterUuid]) {
                            acc[filterUuid] = [];
                        }
                        acc[filterUuid].push(filterItemUuid);
                        return acc;
                    }, {});

                for (const [filterUuid, updateData] of Object.entries(this.selectedAttributesForUpdate)) {
                    let filterItems = updateData.filterItems.length > 0 ? updateData.filterItems : null;

                    if (updateData.action.key === 'clear') {
                        delete productFilterItemUuidsByFilterUuid[filterUuid];
                    } else if (Array.isArray(filterItems) && filterItems.length > 0) {
                        const filterItemUuids = filterItems.map(i => i.uuid);

                        if (updateData.action.key === 'change') {
                            productFilterItemUuidsByFilterUuid[filterUuid] = filterItemUuids;
                        } else if (updateData.action.key === 'add') {
                            productFilterItemUuidsByFilterUuid[filterUuid] = [
                                ...(productFilterItemUuidsByFilterUuid[filterUuid] || []),
                                ...filterItemUuids
                            ].filter((value, index, self) => self.indexOf(value) === index);
                        } else if (updateData.action.key === 'remove') {
                            productFilterItemUuidsByFilterUuid[filterUuid] = (productFilterItemUuidsByFilterUuid[filterUuid] || [])
                                .filter(uuid => !filterItemUuids.includes(uuid));

                            if (productFilterItemUuidsByFilterUuid[filterUuid].length === 0) {
                                delete productFilterItemUuidsByFilterUuid[filterUuid];
                            }
                        }
                    }
                }

                const productFilterItemUuids = [
                    ...new Set(
                        Object.values(productFilterItemUuidsByFilterUuid).flat()
                    )
                ];

                return new HttpRequest("PATCH", `/integrations/products/${product.uuid}`, {
                    filter_item_uuids: productFilterItemUuids
                });
            });
        },
        async saveProductsFilterItems() {
            const requests = this.buildSaveRequests();

            const completed = await this.task.run(async ({setTotal, tick}) => {
                setTotal(requests.length);

                await new HttpSender().sendRequestsInBatchesSuccessively(
                    requests,
                    () => tick(),
                );
            });

            if (!completed) {
                return;
            }

            this.$emit("updated:filter-items");
            this.hide();
        }
    },
    computed: {
        filteredFilters() {
            return this.filters.filter(filter => {
                if (this.selectedOccupancyFilter.key === 'only_filled') {
                    return this.filterItemsByFilterUuid[filter.uuid] !== undefined;
                } else if (this.selectedOccupancyFilter.key === 'only_empty') {
                    return this.filterItemsByFilterUuid[filter.uuid] === undefined;
                }
                return true;
            });
        }
    },
    mounted() {
        this.$refs.Modal.show();
        this.loadProducts();
    },
    beforeUnmount() {
        this.task.cancel();
    }
}
</script>